import React, { useState, useRef } from 'react';
import emailjs from '@emailjs/browser';
import './ContactSection.css';

const EMAILJS_SERVICE_ID  = 'service_v06l9c9';
const EMAILJS_TEMPLATE_ID = 'template_h5i35eq';
const EMAILJS_PUBLIC_KEY  = 'uyhW5UG84CxjdPt-8';

const info = [
  { icon: 'bi-envelope',     label: 'Email Address', val: 'cropguard@iub.edu.pk', sub: '' },
  { icon: 'bi-geo-alt',      label: 'Location',      val: 'The Islamia University of Bahawalpur', sub: 'Faculty of Computing, Dept. of Software Engineering' },
  { icon: 'bi-clock',        label: 'Support Hours', val: 'Mon – Fri: 9:00 AM – 5:00 PM', sub: 'Session Fall 2022 – 2026' },
  { icon: 'bi-person-check', label: 'Supervisor',    val: 'Mam Rubab Sheikh', sub: 'SENG-4301 Project Supervisor' },
];

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validate(form) {
  const errors = {};
  if (!form.name.trim()) {
    errors.name = 'Name is required.';
  } else if (form.name.trim().length < 3) {
    errors.name = 'Name must be at least 3 characters.';
  }
  if (!form.email.trim()) {
    errors.email = 'Email is required.';
  } else if (!emailRegex.test(form.email.trim())) {
    errors.email = 'Please enter a valid email address.';
  }
  if (!form.subject) {
    errors.subject = 'Please select a subject.';
  }
  if (!form.message.trim()) {
    errors.message = 'Message is required.';
  } else if (form.message.trim().length < 10) {
    errors.message = 'Message must be at least 10 characters.';
  }
  return errors;
}

const errorStyle = {
  color: '#ff8a8a',
  fontSize: '.8rem',
  marginTop: 4,
  display: 'block',
};

export default function ContactSection() {
  const [form,    setForm]    = useState({ name: '', email: '', subject: '', message: '' });
  const [errors,  setErrors]  = useState({});
  const [touched, setTouched] = useState({});
  const [status,  setStatus]  = useState('idle'); // idle | sending | sent | error
  const formRef = useRef();

  const handleChange = (field, value) => {
    const updated = { ...form, [field]: value };
    setForm(updated);
    if (touched[field]) {
      const newErrors = validate(updated);
      setErrors(prev => ({ ...prev, [field]: newErrors[field] }));
    }
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    const newErrors = validate(form);
    setErrors(prev => ({ ...prev, [field]: newErrors[field] }));
  };

  const send = async () => {
    // Touch all fields
    setTouched({ name: true, email: true, subject: true, message: true });
    const newErrors = validate(form);
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setStatus('sending');
    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          from_name:  form.name,
          from_email: form.email,
          subject:    form.subject,
          message:    form.message,
          to_email:   'shah2304shah@gmail.com',
        },
        EMAILJS_PUBLIC_KEY
      );
      setStatus('sent');
      setForm({ name: '', email: '', subject: '', message: '' });
      setErrors({});
      setTouched({});
      setTimeout(() => setStatus('idle'), 5000);
    } catch (err) {
      console.error('EmailJS error:', err);
      setStatus('error');
      setTimeout(() => setStatus('idle'), 4000);
    }
  };

  const inputStyle = (field) => ({
    borderColor: errors[field] ? 'rgba(255,80,80,.6)' : undefined,
  });

  return (
    <section id="contact">
      <div className="container">
        <div className="text-center mb-5 reveal">
          <span className="section-label">Get In Touch</span>
          <h2 className="section-title">
            Contact <span className="accent">Us</span>
          </h2>
          <p className="section-desc mx-auto">
            Have a question about crop disease or the system? Reach out and we'll respond promptly.
          </p>
        </div>

        <div className="row g-5">
          {/* ── Info ── */}
          <div className="col-lg-5 reveal">
            {info.map((c, i) => (
              <div className="contact-card" key={i}>
                <div className="contact-icon"><i className={`bi ${c.icon}`}></i></div>
                <div>
                  <div style={{ fontSize: '.82rem', color: 'var(--text-muted)', marginBottom: 4 }}>{c.label}</div>
                  <div style={{ color: 'var(--cream)', fontWeight: 500 }}>{c.val}</div>
                  {c.sub && <div style={{ fontSize: '.85rem', color: 'var(--text-muted)' }}>{c.sub}</div>}
                </div>
              </div>
            ))}
          </div>

          {/* ── Form ── */}
          <div className="col-lg-7 reveal">
            {status === 'sent' ? (
              <div className="contact-success">
                <div style={{ fontSize: '3rem', marginBottom: 12 }}>📨</div>
                <h4 style={{ color: 'var(--cream)', fontFamily: 'Playfair Display,serif', marginBottom: 8 }}>Message Sent!</h4>
                <p style={{ color: 'var(--text-muted)' }}>
                  Your message has been delivered to <strong style={{ color: 'var(--green-accent)' }}>shah2304shah@gmail.com</strong>.<br />
                  We typically respond within 24 hours.
                </p>
              </div>
            ) : (
              <div className="detect-box" ref={formRef}>
                <h4 style={{ color: 'var(--cream)', marginBottom: 6, fontFamily: 'Playfair Display,serif' }}>Send a Message</h4>
                <p style={{ color: 'var(--text-muted)', fontSize: '.9rem', marginBottom: 24 }}>
                  We typically respond within 24 hours on working days.
                </p>

                {status === 'error' && (
                  <div style={{ background: 'rgba(255,80,80,.1)', border: '1px solid rgba(255,80,80,.3)', borderRadius: 10, padding: '12px 16px', marginBottom: 16, color: '#ff8a8a', fontSize: '.88rem' }}>
                    <i className="bi bi-exclamation-circle me-2"></i>
                    Failed to send. Please check EmailJS setup or try again.
                  </div>
                )}

                <div className="row g-3">
                  <div className="col-md-6">
                    <input
                      className="form-control-green"
                      placeholder="Your Full Name"
                      value={form.name}
                      style={inputStyle('name')}
                      onChange={e => handleChange('name', e.target.value)}
                      onBlur={() => handleBlur('name')}
                    />
                    {errors.name && <span style={errorStyle}><i className="bi bi-exclamation-circle me-1"></i>{errors.name}</span>}
                  </div>
                  <div className="col-md-6">
                    <input
                      className="form-control-green"
                      placeholder="Email Address"
                      type="email"
                      value={form.email}
                      style={inputStyle('email')}
                      onChange={e => handleChange('email', e.target.value)}
                      onBlur={() => handleBlur('email')}
                    />
                    {errors.email && <span style={errorStyle}><i className="bi bi-exclamation-circle me-1"></i>{errors.email}</span>}
                  </div>
                  <div className="col-12">
                    <select
                      className="form-control-green"
                      value={form.subject}
                      style={inputStyle('subject')}
                      onChange={e => handleChange('subject', e.target.value)}
                      onBlur={() => handleBlur('subject')}
                    >
                      <option value="" disabled>Subject</option>
                      <option>Disease Detection Inquiry</option>
                      <option>Technical Support</option>
                      <option>Collaboration / Research</option>
                      <option>System Feedback</option>
                      <option>Other</option>
                    </select>
                    {errors.subject && <span style={errorStyle}><i className="bi bi-exclamation-circle me-1"></i>{errors.subject}</span>}
                  </div>
                  <div className="col-12">
                    <textarea
                      className="form-control-green"
                      placeholder="Write your message here..."
                      value={form.message}
                      style={inputStyle('message')}
                      onChange={e => handleChange('message', e.target.value)}
                      onBlur={() => handleBlur('message')}
                    />
                    {errors.message && <span style={errorStyle}><i className="bi bi-exclamation-circle me-1"></i>{errors.message}</span>}
                  </div>
                  <div className="col-12">
                    <button
                      className="btn-primary-green w-100 justify-content-center"
                      onClick={send}
                      disabled={status === 'sending'}
                      style={{ borderRadius: 12, padding: '14px', border: 'none', opacity: status === 'sending' ? .7 : 1 }}
                    >
                      {status === 'sending'
                        ? <><i className="bi bi-hourglass-split me-2"></i>Sending...</>
                        : <><i className="bi bi-send me-2"></i>Send Message</>
                      }
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
